import * as t from 'io-ts';

import {addQueryToPath} from '../../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {makeApiType} from '../../../../libs/shared/core/src/models/api/apiTypes';
import {fetchApiAsync} from '../../../../libs/shared/core/src/services/http/http';
import {usePushEvent} from '../../../../libs/shared/core/src/services/push/pushContext';
import {buildAppQueries} from '../../../../libs/shared/core/src/services/query/appQueryHelpers';
import {useAppQuery} from '../../../../libs/shared/core/src/services/query/useAppQuery';
import {AppPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {PushEventTypesEnum} from '../../../../srcCommon/push/pushHelpers';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {AppState} from '../../../store/appState';
import {
  bestPracticesCourseTaskSetupGuideUuid,
  getSharedWorkspaceTaskIsComplete,
  rulesInSetupGuideAdminOnboardingTaskDefinitions,
} from './adminOnboardingTasks';
import {fluOnboardingTaskDefinitions} from './fluOnboardingTasks';
import {getAdminOnboardingTasks, getFluOnboardingTasks} from './onboardingTaskSelectors';
import {
  AdminSetupGuideTasksAndProgress,
  CompletedCourses,
  FluSetupGuideTasksAndProgress,
} from './onboardingTypes';

export function useSharedWorkspaceTaskIsComplete(): boolean | undefined {
  return useAppSelector((state: AppState) => getSharedWorkspaceTaskIsComplete(state));
}

export function useAdminOnboardingTasks(
  completedCourses: CompletedCourses | undefined,
): AdminSetupGuideTasksAndProgress {
  return useAppSelector((state: AppState) =>
    getAdminOnboardingTasks(state, {
      taskDefinitions: rulesInSetupGuideAdminOnboardingTaskDefinitions,
      completedCourses,
    }),
  );
}

export function useFluOnboardingTasks(props: {panelId: AppPanelId}): FluSetupGuideTasksAndProgress {
  const {panelId} = props;
  return useAppSelector((state: AppState) =>
    getFluOnboardingTasks(state, {
      taskDefinitions: fluOnboardingTaskDefinitions,
      panelId,
    }),
  );
}

const RCompletedCourses = t.record(t.string, t.boolean);

const parseCompletedCourses = (src: any) => src.completed_courses;

const RCompletedCourse = t.type({
  courseUuid: t.string,
});

const parseCompletedCourse = (src: any) => ({courseUuid: src.course_uuid});

const ApiCompletedCourse = makeApiType('completedCourse', RCompletedCourse, parseCompletedCourse);

const ApiCompletedCourses = makeApiType('completedCourses', RCompletedCourses, parseCompletedCourses);

const lmsCompletedCoursesQueries = buildAppQueries('lmsCompletedCourses', {
  forCourseUuids: (courseUuids: ReadonlyArray<string>) => ({
    queryKey: courseUuids,
    queryFn: async ({company, cancelToken}) =>
      fetchApiAsync(
        ApiCompletedCourses,
        addQueryToPath(`${company.url}/lms/completed-courses`, {
          courses: courseUuids.join(','),
        }),
        {cancelToken},
      ),
  }),
});

export const useQueryLmsCourses = () => {
  const {data: completedCourses, refetch: refetchCompletedCourses} = useAppQuery(
    lmsCompletedCoursesQueries.forCourseUuids([bestPracticesCourseTaskSetupGuideUuid]),
  );
  usePushEvent(PushEventTypesEnum.LMS_COURSE_COMPLETED, ApiCompletedCourse, () => refetchCompletedCourses());
  return {
    completedCourses,
    refetchCompletedCourses,
  };
};
